import { useState } from "react";
import { ChevronLeft, ChevronRight, Clock, MapPin, User, Pencil, Trash2, List, CalendarDays, Send } from "lucide-react";
import { sessionTag, IssueBadges } from "@/components/ShiftCheck";
import CertifiedBadge from "@/components/CertifiedBadge";

const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => new Date(`${s}T00:00:00`);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));
const mins = (t, dflt) => { const [h, m] = String(t || dflt).split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const fmtDay = (d) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const HOUR_START = 6, HOUR_END = 22, ROW = 40;

function Toggle({ value, options, onChange, testid }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid={testid}>
      {options.map(([k, label, Icon]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${value === k ? "bg-[#1A3A6D] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          {Icon && <Icon size={13} />}{label}
        </button>
      ))}
    </div>
  );
}

function ShiftActions({ s, onEdit, onDelete, prefix }) {
  if (!onEdit && !onDelete) return null;
  return (
    <span className="inline-flex gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
      {onEdit && <button onClick={() => onEdit(s)} className="text-slate-400 hover:text-[#1A3A6D]" data-testid={`${prefix}-edit-${s.id}`}><Pencil size={13} /></button>}
      {onDelete && <button onClick={() => onDelete(s)} className="text-rose-400 hover:text-rose-600" data-testid={`${prefix}-del-${s.id}`}><Trash2 size={13} /></button>}
    </span>
  );
}

function ListRow({ s, showClinician, onEdit, onDelete, onRequest, prefix }) {
  const cr = s.change_request?.status === "Pending" ? s.change_request : null;
  return (
    <div className={`flex items-center gap-2.5 flex-wrap rounded-lg px-3 py-2 text-[12.5px] ${s.issues?.length ? "bg-rose-50/50 border border-rose-100" : "bg-slate-50"}`} data-testid={`${prefix}-shift-${s.id}`}>
      <Clock size={13} className="text-teal-600 shrink-0" />
      <span className="font-semibold whitespace-nowrap">{s.start}–{s.end}</span>
      {showClinician && s.clinician_name && <span className="inline-flex items-center gap-1 font-semibold text-[#1A3A6D]">{s.clinician_name}<CertifiedBadge comp={s.clinician_compliance} size={13} testid={`${prefix}-certified-${s.id}`} /></span>}
      <span className="inline-flex items-center gap-1 text-slate-700 min-w-0"><User size={12} className="text-slate-400 shrink-0" /><span className="break-words">{s.patient_name || "General shift"}</span></span>
      {s.session_info && <span className="rounded-full bg-sky-50 border border-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800 whitespace-nowrap" data-testid={`${prefix}-session-${s.id}`}>{sessionTag(s.session_info)}</span>}
      <IssueBadges issues={s.issues} testid={`${prefix}-issues-${s.id}`} />
      {cr && <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`${prefix}-cr-${s.id}`}>Change requested → {cr.date}</span>}
      <span className="inline-flex items-center gap-1 text-slate-500 sm:ml-auto min-w-0"><MapPin size={12} className="text-slate-400 shrink-0" /><span className="break-words">{s.location}</span></span>
      {onRequest && s.patient_id && !cr && <button onClick={() => onRequest(s)} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-teal-700 hover:underline" data-testid={`${prefix}-request-${s.id}`}><Send size={12} /> Request change</button>}
      <ShiftActions s={s} onEdit={onEdit} onDelete={onDelete} prefix={prefix} />
    </div>
  );
}

function withLanes(items) {
  const sorted = [...items].sort((a, b) => mins(a.start, "09:00") - mins(b.start, "09:00"));
  const laneEnds = [];
  const out = sorted.map((s) => {
    const st = mins(s.start, "09:00"), en = Math.max(st + 30, mins(s.end, "17:00"));
    let lane = laneEnds.findIndex((e) => e <= st);
    if (lane < 0) { lane = laneEnds.length; laneEnds.push(en); } else laneEnds[lane] = en;
    return { s, st, en, lane };
  });
  return { out, lanes: Math.max(1, laneEnds.length) };
}

function DayColumn({ items, showClinician, onEdit, prefix }) {
  const { out, lanes } = withLanes(items);
  const top0 = HOUR_START * 60;
  return (
    <div className="relative border-l border-slate-100" style={{ height: (HOUR_END - HOUR_START) * ROW }}>
      {Array.from({ length: HOUR_END - HOUR_START }).map((_, i) => (
        <div key={i} className="absolute left-0 right-0 border-t border-dashed border-slate-100" style={{ top: i * ROW }} />
      ))}
      {out.map(({ s, st, en, lane }) => (
        <button key={s.id} onClick={() => onEdit && onEdit(s)} data-testid={`${prefix}-cal-${s.id}`} title={`${s.start}–${s.end} · ${showClinician && s.clinician_name ? `${s.clinician_name} · ` : ""}${s.patient_name || "General shift"} · ${s.location || ""}`}
          className={`absolute rounded-md border px-1.5 py-1 ${s.issues?.length ? "bg-rose-50 border-rose-300" : "bg-teal-50 border-teal-200"} text-left overflow-hidden hover:bg-teal-100 transition-colors ${onEdit ? "cursor-pointer" : "cursor-default"}`}
          style={{ top: Math.max(0, (st - top0) / 60 * ROW), height: Math.max(22, (en - st) / 60 * ROW - 2),
            left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)` }}>
          <div className="text-[10.5px] font-bold text-teal-800 leading-tight">{s.start}–{s.end}</div>
          {showClinician && s.clinician_name && <div className="flex items-center gap-0.5 text-[10.5px] font-semibold text-[#1A3A6D] leading-tight min-w-0"><span className="truncate">{s.clinician_name}</span><CertifiedBadge comp={s.clinician_compliance} size={11} iconOnly testid={`${prefix}-cal-certified-${s.id}`} /></div>}
          <div className="text-[10.5px] text-slate-600 truncate leading-tight">{s.patient_name || "General shift"}</div>
          {s.session_info && <div className="text-[10px] font-semibold text-sky-800 truncate leading-tight">{sessionTag(s.session_info)}</div>}
        </button>
      ))}
    </div>
  );
}

function CalendarGrid({ days, byDate, showClinician, onEdit, prefix }) {
  return (
    <div className="overflow-x-auto" data-testid={`${prefix}-calendar`}>
      <div style={{ minWidth: days.length > 1 ? 760 : 0 }}>
        <div className="grid" style={{ gridTemplateColumns: `48px repeat(${days.length}, minmax(0,1fr))` }}>
          <div />
          {days.map((d) => (
            <div key={ymd(d)} className={`text-center text-[11.5px] font-bold py-1.5 border-b border-slate-100 ${ymd(d) === ymd(new Date()) ? "text-teal-700" : "text-slate-600"}`}>{fmtDay(d)}</div>
          ))}
          <div className="relative" style={{ height: (HOUR_END - HOUR_START) * ROW }}>
            {Array.from({ length: HOUR_END - HOUR_START }).map((_, i) => (
              <div key={i} className="absolute right-1.5 text-[10px] text-slate-400" style={{ top: i * ROW - 6 }}>{pad(HOUR_START + i)}:00</div>
            ))}
          </div>
          {days.map((d) => <DayColumn key={ymd(d)} items={byDate[ymd(d)] || []} showClinician={showClinician} onEdit={onEdit} prefix={prefix} />)}
        </div>
      </div>
    </div>
  );
}

export default function RosterViews({ shifts = [], showClinician = false, onEdit, onDelete, onRequest, prefix = "roster", defaultMode = "week" }) {
  const [mode, setMode] = useState(defaultMode);
  const [view, setView] = useState("list");
  const [anchor, setAnchor] = useState(() => new Date());
  const start = mode === "day" ? anchor : mondayOf(anchor);
  const days = mode === "day" ? [start] : Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const keys = new Set(days.map(ymd));
  const byDate = {};
  shifts.filter((s) => keys.has(s.date)).forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });
  Object.values(byDate).forEach((arr) => arr.sort((a, b) => mins(a.start, "09:00") - mins(b.start, "09:00")));
  const count = Object.values(byDate).reduce((n, a) => n + a.length, 0);
  const step = mode === "day" ? 1 : 7;
  const label = mode === "day" ? fmtDay(start) : `${fmtDay(days[0])} – ${fmtDay(days[6])}`;

  return (
    <div data-testid={`${prefix}-views`}>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Toggle value={mode} onChange={setMode} testid={`${prefix}-mode`} options={[["day", "Daily"], ["week", "Weekly"]]} />
        <Toggle value={view} onChange={setView} testid={`${prefix}-view`} options={[["list", "List", List], ["calendar", "Calendar", CalendarDays]]} />
        <div className="flex items-center gap-1 flex-wrap sm:ml-auto min-w-0">
          <button onClick={() => setAnchor(addDays(anchor, -step))} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600" data-testid={`${prefix}-prev`}><ChevronLeft size={16} /></button>
          <button onClick={() => setAnchor(new Date())} className="px-2.5 py-1 rounded-md border border-slate-200 text-[12px] font-semibold text-slate-600 hover:bg-slate-50" data-testid={`${prefix}-today`}>Today</button>
          <button onClick={() => setAnchor(addDays(anchor, step))} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600" data-testid={`${prefix}-next`}><ChevronRight size={16} /></button>
          <span className="text-[12.5px] font-semibold text-slate-700 ml-1" data-testid={`${prefix}-range`}>{label}</span>
          <span className="text-[11.5px] text-slate-400 ml-1 whitespace-nowrap">· {count} shift{count === 1 ? "" : "s"}</span>
        </div>
      </div>
      {view === "calendar" ? (
        <CalendarGrid days={days} byDate={byDate} showClinician={showClinician} onEdit={onEdit} prefix={prefix} />
      ) : count === 0 ? (
        <p className="text-[12.5px] text-slate-400 py-6 text-center" data-testid={`${prefix}-empty`}>No shifts in this {mode === "day" ? "day" : "week"}.</p>
      ) : (
        <div className="space-y-3" data-testid={`${prefix}-list`}>
          {days.filter((d) => byDate[ymd(d)]).map((d) => (
            <div key={ymd(d)} data-testid={`${prefix}-day-${ymd(d)}`}>
              <div className="text-[11.5px] font-bold text-slate-700 mb-1.5">{fmtDay(d)}</div>
              <div className="space-y-1.5">
                {byDate[ymd(d)].map((s) => <ListRow key={s.id} s={s} showClinician={showClinician} onEdit={onEdit} onDelete={onDelete} onRequest={onRequest} prefix={prefix} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
